import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, FileAudio, Link as LinkIcon, Save, Loader2, X, AlertCircle } from 'lucide-react';

type FilType = 'upload' | 'url';

interface AddFileFormProps {
  navn: string;
  filType: FilType;
  selectedFile: File | null;
  url: string;
  errors: { navn?: string; fil?: string; url?: string };
  loading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onNavnChange: (value: string) => void;
  onFilTypeChange: (type: FilType) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUrlChange: (value: string) => void;
  onRemoveFile: () => void;
  onSubmit: () => void;
}

export const AddFileForm: React.FC<AddFileFormProps> = ({
  navn,
  filType,
  selectedFile,
  url,
  errors,
  loading,
  fileInputRef,
  onNavnChange,
  onFilTypeChange,
  onFileChange,
  onUrlChange,
  onRemoveFile,
  onSubmit,
}) => {
  return (
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="h-1 bg-gradient-to-r from-primary to-blue-400" />
      <div className="p-4 sm:p-6">
        <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <Upload className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold">Legg til fil</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Last opp lydfil eller legg til lenke
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Navn */}
          <div className="space-y-2">
            <Label htmlFor="navn" className="text-sm sm:text-base">
              Navn <span className="text-destructive">*</span>
            </Label>
            <Input
              id="navn"
              value={navn}
              onChange={(e) => onNavnChange(e.target.value)}
              placeholder="F.eks. 'Øvingsopptak' eller 'Live-versjon'"
              className={`bg-slate-50/50 focus:bg-white ${errors.navn ? 'border-destructive' : ''}`}
            />
            {errors.navn && (
              <p className="text-xs sm:text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.navn}
              </p>
            )}
          </div>

          {/* Filtype valg */}
          <div className="space-y-3">
            <Label className="text-sm sm:text-base">
              Hvordan vil du legge ved denne filen? <span className="text-destructive">*</span>
            </Label>
            
            <div className="space-y-2">
              {/* Upload fra maskin */}
              <label
                className={`flex items-start gap-3 p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  filType === 'upload'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="filtype"
                  checked={filType === 'upload'}
                  onChange={() => onFilTypeChange('upload')}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileAudio className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm sm:text-base font-medium">
                      Jeg ønsker å laste opp en fil fra datamaskinen min
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Lydfiler: MP3, WAV, AAC, FLAC, etc. (maks 100MB)
                  </p>
                </div>
              </label>

              {/* URL lenke */}
              <label
                className={`flex items-start gap-3 p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  filType === 'url'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="filtype"
                  checked={filType === 'url'}
                  onChange={() => onFilTypeChange('url')}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <LinkIcon className="h-4 w-4 text-purple-600 shrink-0" />
                    <span className="text-sm sm:text-base font-medium">
                      Jeg vil koble til en URL (Din nettside, iTunes, Amazon, YouTube etc)
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Lenke til lydfil på internett eller strømmetjeneste
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Fil upload (hvis valgt) */}
          {filType === 'upload' && (
            <div className="space-y-2">
              <Label className="text-sm sm:text-base">Velg fil</Label>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={onFileChange}
                className="hidden"
              />
              
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex-1 justify-start gap-2 ${errors.fil ? 'border-destructive' : ''}`}
                >
                  <Upload className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {selectedFile ? selectedFile.name : 'Velg fil fra datamaskin...'}
                  </span>
                </Button>
                
                {selectedFile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onRemoveFile}
                    className="w-full sm:w-auto"
                  >
                    <X className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Fjern</span>
                  </Button>
                )}
              </div>
              
              {selectedFile && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs sm:text-sm">
                  <FileAudio className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="truncate">{selectedFile.name}</span>
                  <span className="text-muted-foreground shrink-0">
                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
              {errors.fil && (
                <p className="text-xs sm:text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.fil}
                </p>
              )}
            </div>
          )}

          {/* URL input (hvis valgt) */}
          {filType === 'url' && (
            <div className="space-y-2">
              <Label htmlFor="url" className="text-sm sm:text-base">
                URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="https://example.com/song.mp3"
                className={`bg-slate-50/50 focus:bg-white ${errors.url ? 'border-destructive' : ''}`}
              />
              {errors.url && (
                <p className="text-xs sm:text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.url}
                </p>
              )}
            </div>
          )}

          {/* Legg til knapp */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={onSubmit}
              size="sm"
              className="w-full sm:w-auto"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Lagrer...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Lagre fil</span>
                  <span className="sm:hidden">Lagre</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

